import type { Broadcast, KhutbahColours, KhutbahLine } from '@noorview/core';
import { KHUTBAH_LOOKS, KHUTBAH_SAMPLE, hhmm, khutbahInk, wordsOf } from '@noorview/core';
import { el } from './dom';
import { INDOPAK_FONT } from './indopak-font';

/**
 * Live Khutbah, full screen and on top of everything (salah blanking, idle and switched-off screens included; only
 * an emergency message shows over it). The Arabic sits above in the Indo-Pak script, the English below in large
 * bold text, both appearing word by word. When a sentence is complete it rises and fades back; the next one builds
 * in its place, and the one before that fades away. A sentence too tall for its place lifts just enough to fit,
 * pushing the faded one up (fainter, or gone when there's no room). The time is always in the top right corner.
 *
 * The look is fixed; only the colours change (background, and the underlines: not heard clearly, Qur'an, hadith).
 */

const TICK = 110;
/** How long a sentence waits for its English when the next one is already in. */
const NO_ENGLISH_MS = 15_000;
/** Where the sentence being spoken starts, the lowest it may reach, and the top of the room above (under the clock). */
const TOP = 0.4, BOTTOM = 0.935, HEAD = 0.17, GAP = 0.047, FADED = 0.6;

interface Shown { seq: number; root: HTMLElement; ar: HTMLElement; en: HTMLElement; ref: HTMLElement; sig: string; arN: number; enN: number; nextAt: number }

function words(target: HTMLElement, segs: KhutbahLine['arabic'] | null, shown: number) {
  const ws = wordsOf(segs ?? []);
  target.replaceChildren();
  ws.forEach((w, i) => {
    const s = el('span', `w k-${w.k}${i < shown ? ' on' : ''}`, w.gap && i < ws.length - 1 && ws[i + 1]!.k === w.k ? `${w.t} ` : w.t);
    target.append(s);
    if (w.gap && i < ws.length - 1 && ws[i + 1]!.k !== w.k) target.append(document.createTextNode(' '));
  });
  return ws.length;
}

export class KhutbahLayer {
  readonly root = el('div', 'nvd-kh');
  private stageEl = el('div', 'kh-lines');
  private clock = el('div', 'kh-clock');
  private waiting = el('div', 'kh-wait', 'The khutbah will appear here as it’s spoken');
  private lines = new Map<number, KhutbahLine>();
  private shown: Shown[] = [];
  private cur = -1;
  private doneAt = 0;
  private acc = { ar: 0, en: 0 };
  private timer: ReturnType<typeof setInterval> | null = null;
  /** The khutbah showing ('sample' in previews). */
  id = '';
  private tz = 'Europe/London';
  private sample = false;
  visible = false;

  constructor() {
    const head = el('div', 'kh-head');
    const tags = el('div', 'kh-tags');
    tags.append(el('span', 'kh-live', 'Live khutbah'), el('span', 'kh-ai', 'English · AI translation'));
    head.append(tags, this.clock);
    this.root.append(head, this.stageEl, this.waiting);
    this.root.setAttribute('aria-live', 'polite');
  }

  /** The khutbah on now for this screen (or none), with the time zone for the clock. */
  set(b: Broadcast | null, tz: string, now: Date, preview = false) {
    this.tz = tz;
    // A preview with nothing said yet just says it's on (the dashboard's previews fill in as it's spoken).
    const wait = preview ? 'Live khutbah on this screen' : 'The khutbah will appear here as it’s spoken';
    if (this.waiting.textContent !== wait) this.waiting.textContent = wait;
    const show = !!b;
    if (b && b.id !== this.id) { this.id = b.id; this.reset(); }
    this.colours(b?.khutbah?.colours ?? KHUTBAH_LOOKS[0]!.colours);
    if (show !== this.visible) {
      this.visible = show;
      this.root.classList.toggle('on', show);
      if (show) this.start(); else { this.stop(); this.id = ''; }
    }
    this.clock.textContent = hhmm(now, tz);
  }

  /** What was said so far (from the screen's live feed). Lines may arrive again, translated or corrected. */
  setLines(list: readonly KhutbahLine[]) {
    for (const l of list) this.lines.set(l.seq, l);
    if (this.visible) this.step(0);
  }

  /** Previews (the dashboard's sheet, the app): the sample khutbah, looping. */
  playSample(colours: KhutbahColours) {
    this.sample = true;
    this.id = 'sample';
    this.reset();
    this.colours(colours);
    this.visible = true;
    this.root.classList.add('on', 'sample');
    this.start();
  }

  colours(c: KhutbahColours) {
    const ink = khutbahInk(c.background);
    const s = this.root.style;
    s.setProperty('--kh-bg', c.background);
    s.setProperty('--kh-ink', ink.ink);
    s.setProperty('--kh-ar', ink.arabic);
    s.setProperty('--kh-faint', ink.faint);
    s.setProperty('--kh-u', c.unclear);
    s.setProperty('--kh-q', c.quran);
    s.setProperty('--kh-h', c.hadith);
  }

  private reset() {
    this.lines.clear();
    this.shown = [];
    this.cur = -1;
    this.doneAt = 0;
    this.stageEl.replaceChildren();
    if (this.sample) KHUTBAH_SAMPLE.forEach((l) => this.lines.set(l.seq, l));
  }

  private start() {
    ensureFont(this.root.ownerDocument);
    if (!this.timer) this.timer = setInterval(() => this.step(TICK), TICK);
    this.step(0);
  }
  private stop() { if (this.timer) clearInterval(this.timer); this.timer = null; }
  destroy() { this.stop(); this.root.remove(); }

  /** One step of the animation: words appear, a finished sentence makes way for the next. */
  private step(ms: number) {
    if (this.sample) this.clock.textContent = hhmm(new Date(), this.tz);
    const seqs = [...this.lines.keys()].sort((a, b) => a - b);
    this.waiting.classList.toggle('on', seqs.length === 0);
    if (!seqs.length) return;
    if (this.cur < 0) this.open(seqs[0]!);
    const cur = this.shown.at(-1)!;
    const line = this.lines.get(cur.seq)!;
    const next = seqs.find((s) => s > cur.seq);
    const behind = seqs.filter((s) => s > cur.seq).length;
    // Catch up when the khutbah is ahead of the screen.
    const fast = behind >= 4 ? 40 : behind >= 2 ? 2 : 1;
    const arTotal = wordsOf(line.arabic).length, enTotal = line.english ? wordsOf(line.english).length : 0;
    this.acc.ar += ms * 7 * fast / 1000;
    if (line.english) this.acc.en += ms * 3.2 * fast / 1000;
    const arN = Math.min(arTotal, cur.arN + Math.floor(this.acc.ar)); this.acc.ar %= 1;
    const enN = Math.min(enTotal, cur.enN + Math.floor(this.acc.en)); this.acc.en %= 1;
    this.paint(cur, line, arN, enN);
    // Done once it's fully spoken and translated (its English grows a few words at a time until then). One whose
    // translation never finishes (the connection dropped) gives way to the next one after a while.
    if (next != null && !cur.nextAt) cur.nextAt = Date.now();
    const finished = line.done !== false && line.english != null;
    const given = !finished && !!cur.nextAt && Date.now() - cur.nextAt > NO_ENGLISH_MS;
    const done = arN >= arTotal && ((finished && enN >= enTotal) || given);
    if (done && !this.doneAt) this.doneAt = Date.now();
    if (!done) this.doneAt = 0;
    // Time to finish reading it once it's all there (more for a long one); less when the screen has fallen behind.
    const hold = behind >= 4 ? 400 : behind >= 2 ? 1000 : Math.min(3500, Math.max(1500, 1200 + enTotal * 60));
    if (done && this.doneAt && Date.now() - this.doneAt >= hold) {
      if (next != null) this.open(next);
      else if (this.sample) { this.reset(); this.open(KHUTBAH_SAMPLE[0]!.seq); }
    }
    this.place();
  }

  private open(seq: number) {
    const root = el('div', 'kh-line'), ar = el('p', 'kh-ar'), en = el('p', 'kh-en'), ref = el('div', 'kh-ref');
    ar.lang = 'ar'; ar.dir = 'rtl';
    root.append(ar, en, ref);
    root.style.transition = 'none';
    root.style.opacity = '0';
    this.stageEl.append(root);
    this.shown.push({ seq, root, ar, en, ref, sig: '', arN: 0, enN: 0, nextAt: 0 });
    // Only the one before stays (faded); older ones have faded out by now.
    while (this.shown.length > 3) this.shown.shift()!.root.remove();
    this.cur = seq;
    this.doneAt = 0;
    this.acc = { ar: 0, en: 0 };
    void root.offsetHeight;
    root.style.transition = '';
  }

  private paint(s: Shown, line: KhutbahLine, arN: number, enN: number) {
    const sig = JSON.stringify([line.arabic, line.english, line.ref]);
    if (sig !== s.sig) {
      s.sig = sig;
      words(s.ar, line.arabic, arN);
      words(s.en, line.english, enN);
      s.ref.textContent = line.ref?.text ?? '';
      s.ref.className = `kh-ref ${line.ref?.k ?? ''}`;
    } else {
      const mark = (box: HTMLElement, from: number, to: number) => { const ws = box.querySelectorAll('.w'); for (let i = from; i < to && i < ws.length; i++) ws[i]!.classList.add('on'); };
      if (arN > s.arN) mark(s.ar, s.arN, arN);
      if (enN > s.enN) mark(s.en, s.enN, enN);
    }
    s.arN = arN; s.enN = enN;
    s.ref.classList.toggle('on', !!line.english && enN >= wordsOf(line.english).length && !!line.ref);
  }

  /** Where each sentence sits: the one being spoken at its line (lifted when it's tall), the one before above it. */
  private place() {
    const H = this.root.clientHeight || 1080;
    const n = this.shown.length;
    const cur = this.shown[n - 1], prev = this.shown[n - 2], gone = this.shown[n - 3];
    if (!cur) return;
    const top = TOP * H, room = (BOTTOM - TOP) * H;
    const shift = Math.max(0, Math.min(top - HEAD * H, cur.root.offsetHeight - room));
    cur.root.style.transform = `translateY(${-shift}px)`;
    cur.root.style.opacity = '1';
    if (prev) {
      const prevTop = top - shift - GAP * H - FADED * prev.root.offsetHeight;
      prev.root.style.transform = `translateY(calc(-100% - ${GAP * H + shift}px)) scale(${FADED})`;
      prev.root.style.opacity = prevTop >= HEAD * H ? '.36' : prevTop >= 0.02 * H ? '.14' : '0';
    }
    if (gone) {
      gone.root.style.transform = `translateY(calc(-100% - ${0.2 * H + shift}px)) scale(.5)`;
      gone.root.style.opacity = '0';
    }
  }
}

let fontAdded: WeakSet<Document> | null = null;
/** The Indo-Pak script, added the first time a khutbah shows. */
function ensureFont(doc: Document) {
  fontAdded ??= new WeakSet();
  if (fontAdded.has(doc)) return;
  fontAdded.add(doc);
  try {
    const FontFaceCtor = (doc.defaultView as (Window & typeof globalThis) | null)?.FontFace;
    if (FontFaceCtor && doc.fonts) {
      const f = new FontFaceCtor('NoorView IndoPak', `url(${INDOPAK_FONT})`, { display: 'block' });
      doc.fonts.add(f);
      void f.load().catch(() => undefined);
      return;
    }
  } catch { /* fall back to a style rule */ }
  const s = doc.createElement('style');
  s.textContent = `@font-face{font-family:"NoorView IndoPak";src:url(${INDOPAK_FONT}) format("woff2");font-display:block}`;
  doc.head.appendChild(s);
}

const FONT = 'var(--nvd-ui-font,"SF Pro Display",-apple-system,"Inter Variable",system-ui,sans-serif)';
export const KHUTBAH_CSS = `
.nvd-kh{position:absolute;inset:0;z-index:1100;background:var(--kh-bg,#000);color:var(--kh-ink,#fff);font-family:${FONT};container-type:size;opacity:0;visibility:hidden;pointer-events:none;transition:opacity .6s ease,visibility 0s linear .6s;overflow:hidden}
.nvd-kh.on{opacity:1;visibility:visible;transition:opacity .6s ease}
.nvd-kh .kh-head{position:absolute;top:min(5cqh,2.8cqw);left:min(5cqh,2.8cqw);right:min(5cqh,2.8cqw);display:flex;justify-content:space-between;align-items:flex-start;z-index:2}
.nvd-kh .kh-tags{display:flex;flex-direction:column;align-items:flex-start;gap:min(1.2cqh,.7cqw);padding-top:min(1.2cqh,.7cqw)}
.nvd-kh .kh-live{font-size:min(2.3cqh,1.3cqw);font-weight:600;line-height:1;padding:min(.8cqh,.45cqw) min(1.5cqh,.85cqw);border:max(1px,min(.16cqh,.09cqw)) solid var(--kh-faint);border-radius:999px}
.nvd-kh .kh-ai{font-size:min(2.1cqh,1.2cqw);font-weight:500;color:var(--kh-faint)}
.nvd-kh .kh-clock{font-size:min(12cqh,6.8cqw);font-weight:600;line-height:.9;font-variant-numeric:tabular-nums;letter-spacing:-.02em}
.nvd-kh .kh-lines{position:absolute;inset:0}
.nvd-kh .kh-line{position:absolute;left:8.6cqw;right:8.6cqw;top:40cqh;text-align:center;transform-origin:50% 100%;transition:transform .9s cubic-bezier(.22,.8,.24,1),opacity .9s ease}
.nvd-kh .kh-ar{margin:0 0 min(1.7cqh,.95cqw);font-family:"NoorView IndoPak","Noto Naskh Arabic Variable","Noto Naskh Arabic","Amiri",serif;font-size:min(4.7cqh,2.65cqw);line-height:1.9;color:var(--kh-ar)}
.nvd-kh .kh-en{margin:0;font-size:min(6.9cqh,3.9cqw);line-height:1.28;font-weight:700;letter-spacing:-.015em}
.nvd-kh .w{opacity:0;filter:blur(min(1.4cqh,.8cqw));transition:opacity .5s ease,filter .5s ease}
.nvd-kh .w.on{opacity:1;filter:none}
.nvd-kh .k-q,.nvd-kh .k-h,.nvd-kh .k-u{text-decoration-line:underline;text-decoration-thickness:max(2px,min(.42cqh,.24cqw));text-underline-offset:min(1.4cqh,.8cqw)}
.nvd-kh .kh-ar :is(.k-q,.k-h,.k-u){text-decoration-thickness:max(1px,min(.28cqh,.16cqw));text-underline-offset:min(1.1cqh,.6cqw)}
.nvd-kh .k-q{text-decoration-style:solid;text-decoration-color:var(--kh-q)}
.nvd-kh .k-h{text-decoration-style:solid;text-decoration-color:var(--kh-h)}
.nvd-kh .k-u{text-decoration-style:dashed;text-decoration-color:var(--kh-u)}
.nvd-kh .kh-ref{margin-top:min(1.9cqh,1.1cqw);font-size:min(2.9cqh,1.65cqw);line-height:1.3;font-weight:500;opacity:0;transition:opacity .5s ease}
.nvd-kh .kh-ref.q{color:var(--kh-q)}.nvd-kh .kh-ref.h{color:var(--kh-h)}
.nvd-kh .kh-ref.on{opacity:1}
.nvd-kh .kh-wait{position:absolute;left:8.6cqw;right:8.6cqw;top:44cqh;text-align:center;font-size:min(3.4cqh,1.9cqw);font-weight:500;color:var(--kh-faint);opacity:0;transition:opacity .6s ease}
.nvd-kh .kh-wait.on{opacity:1}
@media (prefers-reduced-motion:reduce){.nvd-kh .kh-line,.nvd-kh .w{transition:none}}
`;
